import React, {Component} from 'react';
import PropTypes from 'prop-types';
import styles from './GameScreen.module.scss';
import classNames from 'classnames';
import GameTimer from './game/GameTimer';
import gameConstants from './gameConstants';
import {isLastRound, getCurrentTeamIndex, isTeamWin, waitPoses} from './gameHelpers';
import * as audioBundle from './audioBundle';
import posed, {PoseGroup} from 'react-pose';
import {Section, Paper, ScorePanel, ScoreBox, GameEndMask} from './game/commonAnimations';
import GameHeader from './game/GameHeader';
import InfoButton from './game/InfoButton';
import Score from './game/Score';
import Brad from './game/Brad';
import BradColumn from './game/BradColumn';
import SongPrompt from './game/SongPrompt';
import SongPromptMultiple from './game/SongPromptMultiple';
import Timeout from './game/Timeout';
import Lyrics from './game/Lyrics';
import DiscussionSection from './game/DiscussionSection';
import GameEndPicture from './game/GameEndPicture';
import roundRegistry from './roundRegistry';
import audioAnalyser from './audioAnalyser';
import SoundWave from './game/SoundWave';

const tryDurations = [
    gameConstants.tryDuration1,
    gameConstants.tryDuration2,
    gameConstants.tryDuration3,
];

export default class GameScreen extends Component {
    static propTypes = {
        game: PropTypes.object.isRequired,
        onChangeGame: PropTypes.func.isRequired,
    };

    undoEffects = [];
    timers = [];

    componentDidMount() {
        this.addTimeout(() => {
            this.props.onChangeGame({
                ...this.props.game,
                stage: 'wait-try',
                ...this.formCanPlayRecordTimestamp(),
            });
        }, 2000);

        //setTimeout(this.prepareToDiscussion, 1000);
        //setTimeout(this.nextRound, 2000);
        //
        //setTimeout(() => {
        //    this.analyzeDiscussion();
        //}, 2000);
    }

    addTimeout(fn, delay) {
        this.timers.push(setTimeout(fn, delay));
    }

    formCanPlayRecordTimestamp() {
        return {
            canPlayRecordTimestamp: Date.now() + 4 * 1000,
        };
    }

    addEffect(action, undo) {
        this.undoEffects.push(undo);

        return () => {
            if (this.undoEffects.indexOf(undo) >= 0) {
                action();
            }
        };
    }

    componentWillUnmount() {
        this.clearEffects();
        this.timers.forEach(clearTimeout);
    }

    clearEffects() {
        for (const undo of this.undoEffects) {
            undo();
        }
        this.undoEffects = [];
    }

    render() {
        const {game} = this.props;

        const songPromptKey = game.round + '_' + game.try;
        const pose = this.formPose();

        const soundWaveStroke = game.stage === 'listen' ? '#0a6b40' : '#0963f4';

        return (
            <Section pose={pose} initialPose='void' className={styles.gameScreen}>
                {game.playingAudio && (
                    <SoundWave
                        className={styles.soundWave}
                        dataProvider={audioAnalyser}
                        stroke={soundWaveStroke}
                        opacity={0.05}
                        withGain
                    />
                )}

                <Paper className={styles.paper}>
                    <div className={styles.paperAnchor}>
                        <div className={styles.smallIntro}>
                            <div className={styles.smallHeader}>Quest 2:</div>
                            <div className={styles.smallHeader}>We Got Game</div>
                            <div className={styles.smallHeader}>Perpetrating Rhymes</div>
                        </div>

                        <GameHeader {...this.props} />

                        <article className={styles.content}>
                            <BradColumn className={styles.bradColumn}>
                                <Brad
                                    stage={game.stage}
                                    playingAudio={game.playingAudio}
                                    teamDirection={this.getTeamDirection()}
                                    className={styles.brad}
                                    onClick={this.playRecord}
                                />

                                <GameTimer
                                    game={game}
                                    duration={gameConstants.timerDuration}
                                    onAlarm={this.handleWrongAnswer({timeout: true})}
                                />

                                <div className={styles.promptArea}>
                                    <SongPrompt
                                        key={songPromptKey}
                                        {...this.props}
                                        onRightAnswer={this.handleRightAnswer({
                                            multiple: false,
                                        })}
                                    />
                                    <Timeout />
                                </div>
                            </BradColumn>

                            <SongPromptMultiple
                                {...this.props}
                                onWrongAnswer={this.handleWrongAnswer({timeout: false})}
                                onRightAnswer={this.handleRightAnswer({multiple: true})}
                            />

                            <Lyrics {...this.props} />
                        </article>

                        <InfoButton {...this.props} onResetGame={this.resetGame} />

                        <ScorePanel className={styles.scoresPanel}>
                            {game.scores.map(this.renderTeamScorePanel)}
                        </ScorePanel>
                    </div>

                    <DiscussionSection
                        {...this.props}
                        onNext={this.incrementDiscussionStep(1)}
                        onPrevious={this.incrementDiscussionStep(-1)}
                        onClose={this.nextRound}
                    />
                    <GameEndMask className={styles.gameEndMask} />
                    <GameEndPicture className={styles.gameEndMask} />
                </Paper>
            </Section>
        );
    }

    formPose() {
        const {game} = this.props;

        if (!game.playingAudio) {
            return game.stage;
        }

        return game.stage === 'wait-try' ? 'listen' : 'discussion-listen';
    }

    getTeamDirection(teamIndex = -1) {
        if (teamIndex < 0) {
            teamIndex = getCurrentTeamIndex(this.props.game);
        }

        return (teamIndex - 0.5) * 2;
    }

    renderTeamScorePanel = (score, index) => {
        const {game} = this.props;

        const teamDirection = this.getTeamDirection(index);
        const active = index === getCurrentTeamIndex(game);
        const win = isTeamWin(game, index);

        return (
            <ScoreBox key={index} teamDirection={teamDirection}>
                <Score
                    index={index}
                    stage={game.stage}
                    score={score}
                    teamDirection={teamDirection}
                    active={active}
                    tryIndex={game.try}
                    win={win}
                    className={styles.scorePanel}
                />
            </ScoreBox>
        );
    };

    playRecord = () => {
        if (!this.canPlayRecord()) {
            return;
        }

        const {game} = this.props;

        this.props.onChangeGame({
            ...game,
            playingAudio: true,
        });

        if (game.stage === 'discussion-wait') {
            audioBundle.play('analysis');
        }

        const [audioKey, duration] = this.determineAudioToPlay();

        const emitPlayFinished = this.addEffect(this.handlePlayFinished, () =>
            audioBundle.pause(audioKey)
        );
        this.addTimeout(async () => {
            await audioBundle.play(audioKey, {
                duration,
                analyze: true,
            });
            emitPlayFinished();
        }, 1000);
    };

    canPlayRecord() {
        const {game} = this.props;
        const stagesCanPlayAudio = waitPoses;

        if (game.playingAudio) {
            return false;
        }

        if (game.canPlayRecordTimestamp > Date.now()) {
            return false;
        }

        return stagesCanPlayAudio.includes(game.stage);
    }

    determineAudioToPlay() {
        const {game} = this.props;
        const roundInfo = roundRegistry[game.round];

        if (game.stage === 'wait-try') {
            return [roundInfo.audioGame, tryDurations[game.try]];
        } else {
            return [roundInfo.audio, gameConstants.trackDuration];
        }
    }

    handlePlayFinished = () => {
        const {game} = this.props;

        if (game.stage === 'wait-try') {
            this.promptAnswer();
        } else if (game.stage === 'discussion-wait') {
            this.analyzeDiscussion();
        }
    };

    promptAnswer = () => {
        const {game} = this.props;
        const nextStage = game.try !== 2 ? 'prompt' : 'prompt-multiple';

        this.props.onChangeGame({
            ...game,
            stage: nextStage,
            playingAudio: false,
        });
    };

    analyzeDiscussion = () => {
        this.props.onChangeGame({
            ...this.props.game,
            stage: 'discussion-analyze',
            playingAudio: false,
        });

        this.addTimeout(() => {
            this.props.onChangeGame({
                ...this.props.game,
                discussionStep: 0,
            });
        }, 1000);
    };

    handleWrongAnswer = ({timeout}) => () => {
        const {game} = this.props;

        if (game.try < 2) {
            this.nextTry();
        } else {
            this.noWinRound({timeout});
        }
    };

    nextTry() {
        this.props.onChangeGame({
            ...this.props.game,
            stage: 'wrong-answer',
        });

        this.addTimeout(() => {
            this.props.onChangeGame({
                ...this.props.game,
                try: this.props.game.try + 1,
                stage: 'wait-try',
                ...this.formCanPlayRecordTimestamp(),
            });
        }, gameConstants.wrongAnswerTimeout);
    }

    noWinRound({timeout}) {
        const {game} = this.props;

        this.props.onChangeGame({
            ...this.props.game,
            stage: timeout ? 'wrong-answer-multiple-timeout' : 'wrong-answer-multiple',
        });

        this.addTimeout(() => {
            this.prepareToDiscussion();
        }, gameConstants.wrongAnswerTimeout);
    }

    incrementDiscussionStep = increment => () => {
        const {game} = this.props;

        audioBundle.play('discussion');

        this.props.onChangeGame({
            ...this.props.game,
            discussionStep: game.discussionStep + increment,
        });
    };

    nextRound = () => {
        const {game} = this.props;

        this.clearEffects();

        if (isLastRound(game)) {
            audioBundle.play('done');
            this.finishGame();
            return;
        }

        audioBundle.play('nextRound');

        this.props.onChangeGame({
            ...game,
            round: game.round + 1,
            roundChanging: true,
            try: 0,
            playingAudio: false,
            stage: 'wait-try',
            ...this.formCanPlayRecordTimestamp(),
            discussionStep: -1,
        });

        this.addTimeout(() => {
            this.props.onChangeGame({
                ...this.props.game,
                roundChanging: false,
            });
        }, 2000);
    };

    finishGame() {
        const {game} = this.props;

        this.props.onChangeGame({
            ...game,
            stage: 'result',
        });

        this.addTimeout(() => {
            this.props.onChangeGame({
                ...game,
                started: false,
            });
        }, 5000);
    }

    handleRightAnswer = ({multiple}) => () => {
        const {game} = this.props;
        const nextScores = [...game.scores];
        nextScores[getCurrentTeamIndex(game)] += 10;

        audioBundle.play('right');

        this.props.onChangeGame({
            ...game,
            stage: multiple ? 'right-answer-multiple' : 'right-answer',
            scores: nextScores,
        });

        this.addTimeout(this.prepareToDiscussion, gameConstants.rightAnswerTimeout);
    };

    prepareToDiscussion = () => {
        const {game} = this.props;

        this.props.onChangeGame({
            ...game,
            stage: 'discussion-wait',
        });
    };

    resetGame = () => {
        this.props.onChangeGame({
            ...this.props.game,
            started: false,
        });
    };
}
